import { useEffect, useRef, useState } from "react";
import { Check, Copy, X } from "lucide-react";

export const CONTACT_EMAIL = "contact@palnote.ca";

function legacyCopy(text: string): boolean {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/**
 * A link that opens the user's default mail client addressed to CONTACT_EMAIL.
 * If no mail client appears to handle the mailto (page stays visible/focused),
 * it shows a fallback popup with the email address and a Copy Email button.
 */
function buildMailto(subject?: string, body?: string): string {
  const params: string[] = [];
  if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
  if (body) params.push(`body=${encodeURIComponent(body)}`);
  return `mailto:${CONTACT_EMAIL}${params.length ? `?${params.join("&")}` : ""}`;
}

export function ContactButton({
  className,
  children,
  subject,
  body,
}: {
  className?: string;
  children: React.ReactNode;
  subject?: string;
  body?: string;
}) {
  const [showFallback, setShowFallback] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle");
  const mailtoHref = buildMailto(subject, body);

  const mountedRef = useRef(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cleanupListenersRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      cleanupListenersRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (!showFallback) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowFallback(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showFallback]);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    let opened = false;
    const markOpened = () => {
      opened = true;
    };
    window.addEventListener("blur", markOpened, { once: true });
    document.addEventListener("visibilitychange", markOpened, { once: true });
    cleanupListenersRef.current = () => {
      window.removeEventListener("blur", markOpened);
      document.removeEventListener("visibilitychange", markOpened);
    };

    window.location.href = mailtoHref;

    timeoutRef.current = setTimeout(() => {
      cleanupListenersRef.current?.();
      cleanupListenersRef.current = null;
      if (mountedRef.current && !opened && document.visibilityState === "visible") {
        setShowFallback(true);
      }
    }, 800);
  };

  const copyEmail = async () => {
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(CONTACT_EMAIL);
        ok = true;
      } else {
        ok = legacyCopy(CONTACT_EMAIL);
      }
    } catch {
      ok = legacyCopy(CONTACT_EMAIL);
    }
    if (!mountedRef.current) return;
    setCopyState(ok ? "copied" : "error");
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => {
      if (mountedRef.current) setCopyState("idle");
    }, 2500);
  };

  return (
    <>
      <a href={mailtoHref} onClick={handleClick} className={className}>
        {children}
      </a>

      {showFallback && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="contact-popup-title"
        >
          <div
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            onClick={() => setShowFallback(false)}
          />
          <div className="relative w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-2xl shadow-blue-900/10">
            <button
              onClick={() => setShowFallback(false)}
              aria-label="Close"
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
            >
              <X className="h-4 w-4" />
            </button>
            <h3 id="contact-popup-title" className="text-base font-semibold text-slate-900">
              Contact us at:
            </h3>
            <a
              href={mailtoHref}
              className="mt-2 block break-all text-lg font-semibold text-blue-600 hover:text-blue-700"
            >
              {CONTACT_EMAIL}
            </a>
            <button
              onClick={copyEmail}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-all duration-200 hover:-translate-y-0.5 hover:bg-blue-700 active:translate-y-0"
            >
              {copyState === "copied" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copyState === "copied"
                ? "Copied!"
                : copyState === "error"
                  ? "Copy failed — select manually"
                  : "Copy Email"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
